import { useState, useEffect, useMemo, useContext } from "react";
import { useStyles } from "./styles";
import { Box, Grid2, Stack, Typography } from "@mui/material";
import { useTheme } from "@emotion/react";
import constants from "../../../constants/constants.json";
import PropsTypes from "prop-types";
import BasicTable from "components/table";
import EditIcon from "@mui/icons-material/Edit";
import Button from "@mui/material/Button";
import DeleteIcon from "@mui/icons-material/Delete";
import ModalPopup from "components/modalPopup";
import { useNavigate } from "react-router";
import { getToken } from "utils/loadAuth";
import { useAuth0 } from "@auth0/auth0-react";
import Notification from "components/snackbar";
import LoadingButton from "@mui/lab/LoadingButton";
import { fetchExternalRoles, deleteExternalRole } from "./roles.services";
import { UserContext } from "context/userContext";
import SearchField from "components/simpleSearch";

const Roles = ({ isNavbarOpen }) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [externalRoles, setExternalRoles] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [openNotification, setOpenNotification] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("info");
  const [search, setSearch] = useState("");
  const [copiedRoles, setCopiedRoles] = useState([]);
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const navigate = useNavigate();
  const theme = useTheme();
  const classes = useStyles({ theme, isNavbarOpen });
  const contextValue = useContext(UserContext);
  const userContextValue = useMemo(() => contextValue || {}, [contextValue]);
  const loggedInUserType = userContextValue?.loggedInUser?.user_type;

  const handleOpen = (url, role) => {
    navigate(url, {
      state: { activeTab: "roles", roleId: role?.id },
    });
    setSelectedRow(role);
  };

  const handleDelete = async (roleId) => {
    setDeleteOpen(false);
    try {
      setIsDeleting(true);
      const token = await getToken({
        getAccessTokenSilently,
        loginWithRedirect,
      });
      if (!token) {
        throw new Error("No token found");
      }
      await deleteExternalRole({ token, roleId });
      setExternalRoles((prevRoles) =>
        prevRoles.filter((role) => role.id !== roleId)
      );
      setCopiedRoles((prevRoles) =>
        prevRoles.filter((role) => role.id !== roleId)
      );
      setOpenNotification(true);
      setMessage(`${selectedRow?.name} deleted successfully`);
      setSeverity("success");
      setDeleteOpen(false);
    } catch (error) {
      setOpenNotification(true);
      setMessage("Error deleting role " + selectedRow?.name);
      setSeverity("error");
      console.error("Error deleting role:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "name",
      flex: 0.07,
    },
    {
      field: "roleDesc",
      headerName: "description",
      flex: 0.1,
    },
    {
      field: "Actions",
      headerName: "Actions",
      sortable: false,
      flex: 0.05,
      renderCell: (param) => {
        const { id, name, roleDesc, disabled } = param.row;
        return (
          <Box
            data-testid={"action-container"}
            className={classes.actionsContainer}
          >
            <Button
              data-testid={"edit-btn"}
              className={classes.actionBtn}
              onClick={() =>
                handleOpen(
                  "/usermanagement/roles/editrole?roleId=" + id,
                  param.row
                )
              }
              disabled={disabled || isDeleting}
              title={constants.edit}
            >
              <EditIcon />
            </Button>

            <LoadingButton
              data-testid={"delete-btn"}
              className={classes.actionBtn}
              onClick={() => {
                setSelectedRow({ id, name, roleDesc });
                setDeleteOpen(true);
              }}
              disabled={disabled || isDeleting}
              title={constants.delete}
              loading={isDeleting && selectedRow?.id === id}
            >
              <DeleteIcon />
            </LoadingButton>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    const getExternalRoles = async () => {
      setIsLoading(true);
      try {
        const token = await getToken({
          getAccessTokenSilently,
          loginWithRedirect,
        });
        if (!token) {
          throw new Error("No token found");
        }
        const roles = await fetchExternalRoles({ token });

        setExternalRoles(
          roles.map((role) => ({
            id: role.id,
            name: role.roleName,
            serviceName: role.serviceName,
            roleDesc: role.roleDesc,
            disabled: role.disabled || false,
          }))
        );
        setCopiedRoles(
          roles.map((role) => ({
            id: role.id,
            name: role.roleName,
            serviceName: role.serviceName,
            roleDesc: role.roleDesc,
            disabled: role.disabled || false,
          }))
        );
      } catch (error) {
        console.error("Error fetching external roles:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getExternalRoles();
  }, [getAccessTokenSilently, loginWithRedirect]);

  const onSearchChange = (value) => {
    setSearch(value);
    if (!value) {
      setExternalRoles(copiedRoles);
      return;
    }
    const filteredRoles = copiedRoles.filter((role) =>
      role?.name?.toLowerCase().includes(value.toLowerCase())
    );
    setExternalRoles(filteredRoles);
  };

  return (
    <>
      <Grid2 container sx={{ width: "100%" }} data-testid="roles-container">
        <Grid2 size={2}></Grid2>
        <Grid2 size={10} className={classes.wrapper}>
          <Box className={classes.tableWrapper}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography className={classes.title}>
                {constants.roles}
              </Typography>
              {loggedInUserType !== 3 && (
                <Button
                  onClick={() => {
                    handleOpen("/usermanagement/roles/addrole", null);
                  }}
                  className={classes.addRoleButton}
                  data-testid="add-role-button"
                >
                  {constants.addRole}
                </Button>
              )}
            </Box>
            <Stack className={classes.sectionsWrapper} spacing={1}>
              <Box sx={{ width: "100%" }}>
                <SearchField
                  searchValue={search}
                  setSearchValue={(e) => onSearchChange(e)}
                  placeholderText={constants.searchRoleByName}
                />
                <BasicTable
                  showBorder={false}
                  disableColumnMenu
                  isLoading={isLoading}
                  columns={columns}
                  rows={externalRoles}
                />
              </Box>
            </Stack>
          </Box>
        </Grid2>
        <Notification
          message={message}
          open={openNotification}
          setOpen={setOpenNotification}
          severity={severity}
        />
      </Grid2>

      <ModalPopup
        title={constants.deleteRole}
        message={
          <Typography sx={{ textAlign: "center", lineHeight: "1.6" }}>
            <strong>{selectedRow?.name}</strong> {constants.deleteRoleMsg}
          </Typography>
        }
        primaryBtnText="Yes"
        primaryBtnAction={() => handleDelete(selectedRow?.id)}
        secondaryButtonText="No"
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
        secondarybtnAction={() => {
          setDeleteOpen(false);
        }}
      />
    </>
  );
};

Roles.propTypes = {
  isNavbarOpen: PropsTypes.bool,
};

export default Roles;
